import React, { useState, useEffect, Suspense, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import Routes from './pages/Routes';
import './App.css';
import { toast } from 'react-toastify'; // Toast link https://fkhadra.github.io/react-toastify/
import 'react-toastify/dist/ReactToastify.min.css';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import Loader from 'react-loader-spinner';
import { ThemeProvider, createMuiTheme, responsiveFontSizes } from '@material-ui/core';
import NavbarWithSidebar from './pages/site/NavbarWithSidebar';
import { LocalizationProvider } from '@material-ui/pickers';
import MomentUtils from '@material-ui/pickers/adapter/moment';
import { ConfirmationServiceProvider } from './contexts/ConfirmContext';
import CacheBuster from './components/CacheBuster';
import EmployeeInfoContext from './contexts/EmployeeInfoContext';
import ToastContext from './contexts/ToastContext';
import { getMe, deAuthenticate, Employee, getSource, getUsers, getDirectReports, getDefaultClaims, DefaultClaim } from './utils/AccountsAPI';
import { CYMAJob, getAllJobs, getUserAssignedJobs } from './utils/CYMAAPI';
import JobsContext from './contexts/JobsContext';
import UsersContext from './contexts/UsersContext';
import { toastError } from './utils/Functions';
import AxiosConfig, { clearBaseURL } from './utils/AxiosConfig';
import { useUsers } from './hooks/useUsers';
import { useLayoutEffect } from 'react';
import DefaultClaimsContext from './contexts/DefaultClaimsContext';
import { LoadingServiceProvider } from './contexts/LoadingContext';

toast.configure();

export const LoadingIndicator = ({ open = false }) => {
  const { promiseInProgress } = usePromiseTracker();
  const ref = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line

  useLayoutEffect(() => {
    const preventScroll = (e) => {
      if (e.ctrlKey) e.preventDefault();
    };

    ref.current?.addEventListener('wheel', preventScroll);
    var div = ref.current;
    return () => {
      div?.removeEventListener('wheel', preventScroll);
    };
  });

  return promiseInProgress || open ? (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        minHeight: '100%',
        minWidth: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        zIndex: 10000,
      }}
    >
      <div
        style={{
          margin: 'auto',
          display: 'block',
          top: '50%',
          left: '50%',
          position: 'fixed',
          transform: 'translate(-50%,-50%)',
          width: '100px',
        }}
      >
        <Loader type='ThreeDots' color='#1976D2' height={100} width={96} style={{ display: 'flex' }} />
      </div>
    </div>
  ) : null;
};

export const darkTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#1976d2', //'#03a9f4', //blue
    },
    secondary: {
      main: '#f57c00', //'#ff8a65', //orange
    },
    type: 'dark',
  },
});

function App(props) {
  const [employeeInfo, setEmployeeInfo] = useState<Employee | undefined>();
  const [jobs, setJobs] = useState<CYMAJob[]>([]);
  const [refreshJobs, setRefreshJobs] = useState<number>(1);
  const [users, setUsers, updateUser] = useUsers([]);
  const [defaultClaims, setDefaultClaims] = useState<DefaultClaim[]>([]);

  //const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const darkMode = true; // (sessionStorage.getItem('isDarkMode') ?? localStorage.getItem('isDarkMode'))?.toLowerCase() === 'true' ?? true; //prefersDarkMode;
  const [isDarkMode, setIsDarkMode] = useState<boolean>(darkMode);
  let theme = createMuiTheme({
    palette: {
      primary: {
        main: '#1976d2', //'#03a9f4', //blue
      },
      secondary: {
        main: '#f57c00', //'#ff8a65', //orange
      },
      type: isDarkMode ? 'dark' : 'light',
    },
    overrides: {
      MuiTooltip: {
        tooltip: {
          fontSize: '1em',
        },
      },
    },
  });
  theme = responsiveFontSizes(theme);

  const isAuthenticated = sessionStorage.getItem('accessToken') ?? localStorage.getItem('accessToken') ? true : false;

  const employeeId = employeeInfo?.id;
  const isImpersonating = employeeInfo?.isImpersonating;
  const isAccountingUp = employeeInfo?.isInRoles(['Admin', 'Accounting']) ?? false;
  const isAdmin = employeeInfo?.isInRole('Admin') ?? false;

  // get logged in user
  useEffect(() => {
    var source = getSource();
    if (!employeeId && isAuthenticated) {
      trackPromise(
        getMe(source.token).then((user: Employee) => {
          if (isImpersonating) user.isImpersonating = true;
          setEmployeeInfo(user);
          setJobs(user.jobs);
          setUsers(user.directReports);
        })
      ).catch((err) => {
        setEmployeeInfo(undefined);
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        clearBaseURL();
        props.history.push('/Login');
      });
    }

    return source.cancel;
  }, [isAuthenticated, employeeId, isImpersonating, props.history, setUsers]);

  //jobs
  useEffect(() => {
    var source = getSource();
    if (!employeeId || !refreshJobs) return;
    if (isAccountingUp)
      getAllJobs(true, source.token)
        .then((response) => setJobs(response))
        .catch((err) => toastError(toast, err));
    else
      getUserAssignedJobs(employeeId, true, true, true, source.token)
        .then((response) => setJobs(response))
        .catch((err) => toastError(toast, err));

    return () => source.cancel();
  }, [employeeId, isAccountingUp, refreshJobs]);

  //direct reports
  useEffect(() => {
    var source = getSource();
    if (!employeeId) return;
    if (!isAccountingUp)
      getDirectReports(employeeId, false, false, true, false, source.token)
        .then((dr) => setUsers(dr.sort((a, b) => (a.userName < b.userName ? -1 : 1))))
        .catch((err) => toastError(toast, err));
    else
      getUsers(true, false, false, false, false, false, false, source.token)
        .then((response) => setUsers(response.sort((a, b) => (a.userName < b.userName ? -1 : 1))))
        .catch((err) => toastError(toast, err));

    return () => source.cancel();
  }, [employeeId, isAccountingUp, isAdmin, setUsers]);

  //refreshes
  useEffect(() => {
    var refreshHandle, meHandle;
    let source = getSource();

    if (isAuthenticated)
      refreshHandle = setInterval(() => {
        AxiosConfig.refresh();
      }, 30 * 60 * 1000); //every 30 m

    if (isAuthenticated)
      meHandle = setInterval(() => {
        getMe(source.token)
          .then((user: Employee) => {
            setEmployeeInfo(user);
          })
          .catch((err) => toastError(toast, err));
      }, 5 * 60 * 1000); //every 5 mins

    return () => {
      clearInterval(refreshHandle);
      clearInterval(meHandle);
      source.cancel();
    };
  }, [isAuthenticated]);

  //default claims
  useEffect(() => {
    let source = getSource();

    if (isAuthenticated)
      getDefaultClaims()
        .then((claims) => setDefaultClaims(claims))
        .catch((err) => toastError(toast, err));

    return source.cancel;
  }, [isAuthenticated]);

  //Sets
  const userHasAuthenticated = (authenticated: boolean, location: string, empInfo: Employee) => {
    if (authenticated) props.history.push(location && location !== '/Login' ? location ?? '' : empInfo.getClaimValue('defaultHome') ?? '/');
  };

  //deauth for logouts
  const handleLogout = (event) => {
    deAuthenticate()
      .catch(() => Promise.resolve())
      .then(() => {
        setEmployeeInfo(undefined);
        toast.dismiss();

        sessionStorage.clear();
        localStorage.clear();
        props.history.push('/Login');
      });
  };

  const toggleTheme = () => {
    sessionStorage.setItem('isDarkMode', (!isDarkMode).toString());
    localStorage.setItem('isDarkMode', (!isDarkMode).toString());
    setIsDarkMode(!isDarkMode);
  };

  //pass all these props to children
  const childProps = {
    isAuthenticated: isAuthenticated,
    userHasAuthenticated: userHasAuthenticated,
    employeeInfo: employeeInfo,
    toast,
    toggleTheme,
    isDarkMode,
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <ConfirmationServiceProvider theme={theme}>
          <LoadingServiceProvider theme={theme}>
            <LocalizationProvider dateAdapter={MomentUtils}>
              <EmployeeInfoContext.Provider value={[employeeInfo, setEmployeeInfo]}>
                <ToastContext.Provider value={toast}>
                  <JobsContext.Provider value={[jobs, setJobs, () => setRefreshJobs((r) => r + 1)]}>
                    <UsersContext.Provider value={[users, setUsers, updateUser]}>
                      <DefaultClaimsContext.Provider value={[defaultClaims, setDefaultClaims]}>
                        <NavbarWithSidebar
                          isAuthenticated={isAuthenticated}
                          handleLogout={handleLogout}
                          history={props.history}
                          toggleTheme={toggleTheme}
                          isDarkMode={isDarkMode}
                          toast={toast}
                        >
                          <Suspense fallback={<LoadingIndicator open={true} />}>
                            <Routes childProps={childProps} isAuthenticated={isAuthenticated} />
                          </Suspense>
                        </NavbarWithSidebar>
                        <LoadingIndicator />
                      </DefaultClaimsContext.Provider>
                    </UsersContext.Provider>
                  </JobsContext.Provider>
                </ToastContext.Provider>
              </EmployeeInfoContext.Provider>
            </LocalizationProvider>
          </LoadingServiceProvider>
        </ConfirmationServiceProvider>
      </ThemeProvider>
      <CacheBuster style={{ display: 'none' }} />
    </>
  );
}

export default withRouter(App);
